<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useCalendarStore } from 'dashboard/stores/calendar';
import { useRemindersStore } from 'dashboard/stores/reminders';
import { runCustomAutomations } from 'dashboard/composables/useCustomAutomationRunner';

import Button from 'dashboard/components-next/button/Button.vue';
import EventCreateDialog from 'dashboard/components-next/Calendar/EventCreateDialog.vue';
import ReminderCreateDialog from 'dashboard/components-next/Reminders/ReminderCreateDialog.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    default: undefined,
  },
});

const { t, locale } = useI18n();
const store = useStore();
const calendarStore = useCalendarStore();
const remindersStore = useRemindersStore();

const contactGetter = useMapGetter('contacts/getContact');
const contact = computed(() => contactGetter.value(props.contactId) || {});
const contactPrefill = computed(() =>
  props.contactId ? { id: props.contactId, name: contact.value.name } : null
);

const eventDialogRef = ref(null);
const reminderDialogRef = ref(null);

const includesContact = item =>
  props.contactId != null &&
  (item.contactIds || []).map(String).includes(String(props.contactId));

const reminders = computed(() => remindersStore.list.filter(includesContact));
const events = computed(() => calendarStore.list.filter(includesContact));

const STATUS_LABEL_KEY = {
  pending: 'REMINDERS.STATUS.PENDING',
  fired: 'REMINDERS.STATUS.FIRED',
  done: 'REMINDERS.STATUS.DONE',
};
const statusLabel = status =>
  t(STATUS_LABEL_KEY[status] || STATUS_LABEL_KEY.pending);

const formatDateTime = value => {
  if (!value) return '';
  return new Date(value).toLocaleString(locale.value.replace('_', '-'), {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const eventWhen = evt =>
  `${evt.date}${evt.startTime ? ` ${evt.startTime}` : ''}`;

// Context for the calendar automation engine (mirrors CalendarIndex).
const calendarAutomationContext = evt => {
  const startMs = new Date(`${evt.date}T${evt.startTime || '00:00'}`).getTime();
  const totalMinutes = Math.max(0, Math.round((startMs - Date.now()) / 60000));
  return {
    title: evt.title,
    labels: evt.labels || [],
    agent_ids: evt.agentIds || [],
    contact_ids: evt.contactIds || (evt.contacts || []).map(c => c.id),
    starts_at: `${evt.date}T${evt.startTime || '00:00'}`,
    days: totalMinutes / 1440,
    hours: totalMinutes / 60,
    minutes: totalMinutes,
  };
};

const openCreateEvent = () =>
  eventDialogRef.value?.openForCreate(null, contactPrefill.value);
const openEditEvent = evt => eventDialogRef.value?.openForEdit(evt);
const openCreateReminder = () =>
  reminderDialogRef.value?.openForCreate({ contact: contactPrefill.value });
const openEditReminder = reminder =>
  reminderDialogRef.value?.openForEdit(reminder);

const onEventSave = async payload => {
  if (payload.id) {
    const { id, ...patch } = payload;
    await calendarStore.update(id, patch);
    runCustomAutomations('calendar_event_updated', calendarAutomationContext(patch));
    useAlert(t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.EVENT_UPDATED'));
  } else {
    const { id, ...rest } = payload;
    await calendarStore.create(rest);
    runCustomAutomations('calendar_event_created', calendarAutomationContext(rest));
    useAlert(t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.EVENT_CREATED'));
  }
  eventDialogRef.value?.onSuccess();
};

const onEventRemove = async id => {
  const removed = calendarStore.getEvent(id);
  await calendarStore.remove(id);
  if (removed) {
    runCustomAutomations(
      'calendar_event_deleted',
      calendarAutomationContext(removed)
    );
  }
  eventDialogRef.value?.onSuccess();
  useAlert(t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.EVENT_DELETED'));
};

const onReminderSave = async payload => {
  if (payload.id) {
    const { id, ...patch } = payload;
    await remindersStore.update(id, { ...patch, status: 'pending', firedAt: null });
    useAlert(t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.REMINDER_UPDATED'));
  } else {
    const { id, ...rest } = payload;
    await remindersStore.create(rest);
    useAlert(t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.REMINDER_CREATED'));
  }
  reminderDialogRef.value?.onSuccess();
};

const onReminderRemove = async id => {
  await remindersStore.remove(id);
  reminderDialogRef.value?.onSuccess();
  useAlert(t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.REMINDER_DELETED'));
};

const removeEvent = id => onEventRemove(id);
const removeReminder = id => onReminderRemove(id);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  calendarStore.fetch();
  remindersStore.fetch();
});
</script>

<template>
  <div class="flex flex-col gap-4 px-1 py-2">
    <!-- Events -->
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium uppercase text-n-slate-10">
          {{ t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.EVENTS') }}
        </span>
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-plus"
          @click="openCreateEvent"
        />
      </div>
      <span v-if="!events.length" class="text-xs text-n-slate-10">
        {{ t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.NO_EVENTS') }}
      </span>
      <div
        v-for="evt in events"
        :key="`e-${evt.id}`"
        class="flex items-start gap-2 p-2 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-alpha-1 cursor-pointer"
        @click="openEditEvent(evt)"
      >
        <span class="i-lucide-calendar size-3.5 mt-0.5 text-n-slate-10" />
        <div class="flex flex-col flex-1 min-w-0 gap-0.5">
          <div class="flex items-center gap-1.5">
            <span class="text-[10px] font-mono text-n-slate-10">
              {{ evt.publicId }}
            </span>
            <span class="text-xs font-medium truncate text-n-slate-12">
              {{ evt.title }}
            </span>
          </div>
          <span class="text-[11px] text-n-slate-10">{{ eventWhen(evt) }}</span>
        </div>
        <Button
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash-2"
          @click.stop="removeEvent(evt.id)"
        />
      </div>
    </div>

    <!-- Reminders -->
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium uppercase text-n-slate-10">
          {{ t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.REMINDERS') }}
        </span>
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-plus"
          @click="openCreateReminder"
        />
      </div>
      <span v-if="!reminders.length" class="text-xs text-n-slate-10">
        {{ t('CONVERSATION_SIDEBAR.REMINDERS_EVENTS.NO_REMINDERS') }}
      </span>
      <div
        v-for="reminder in reminders"
        :key="`r-${reminder.id}`"
        class="flex items-start gap-2 p-2 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-alpha-1 cursor-pointer"
        @click="openEditReminder(reminder)"
      >
        <span class="i-lucide-bell size-3.5 mt-0.5 text-n-amber-11" />
        <div class="flex flex-col flex-1 min-w-0 gap-0.5">
          <div class="flex items-center gap-1.5">
            <span class="text-[10px] font-mono text-n-slate-10">
              {{ reminder.publicId }}
            </span>
            <span
              class="text-xs font-medium truncate text-n-slate-12"
              :class="{ 'line-through text-n-slate-10': reminder.status === 'done' }"
            >
              {{ reminder.title }}
            </span>
          </div>
          <span class="text-[11px] text-n-slate-10">
            {{ formatDateTime(reminder.triggerAt) }} · {{ statusLabel(reminder.status) }}
          </span>
        </div>
        <Button
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash-2"
          @click.stop="removeReminder(reminder.id)"
        />
      </div>
    </div>

    <EventCreateDialog
      ref="eventDialogRef"
      @save="onEventSave"
      @remove="onEventRemove"
    />
    <ReminderCreateDialog
      ref="reminderDialogRef"
      @save="onReminderSave"
      @remove="onReminderRemove"
    />
  </div>
</template>
